import { PromptModal } from '@/components/prompt-modal'
import { Button } from '@/components/ui'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatPartOfDay } from '../part-of-day'
import type { ApprovalRestPeriodNode } from '../../types.graphql'

export type RestAwardKind = 'diet' | 'salaryDay'

interface Props {
  restPeriod: ApprovalRestPeriodNode
  title: string
  /** False once a leader has approved the shift the rest hangs off. */
  canEdit: boolean
  onChoose: (kind: RestAwardKind | null) => void
}

/**
 * What a rest carries, and which of the two to open.
 *
 * It opens whether or not the rest can still be changed. A calendar bar looks
 * clickable either way, so a locked rest that simply does nothing reads as
 * broken — this shows the figures and says why they are fixed instead.
 *
 * Deliberately free of mutation hooks: it renders inside the prompt portal,
 * which sits outside the settings store, so it can only pick — the page runs
 * the write afterwards.
 */
export const RestAwardChooser: React.FC<Props> = ({
  restPeriod,
  title,
  canEdit,
  onChoose,
}) => {
  const translations = useTranslate({
    diet: 'common.diet',
    salaryDay: 'common.salary-day',
    locked: 'salary.locked-by-manager-approval',
    none: 'common.none',
  })

  const current = (
    award:
      | { partOfDay?: number | null; salaryCode?: { name: string } | null }
      | null
      | undefined
  ) =>
    award?.salaryCode && (award.partOfDay ?? 0) > 0
      ? `${formatPartOfDay(award.partOfDay ?? 0)} ${award.salaryCode.name}`
      : translations.none

  const showSalaryDay = restPeriod.salarySetting?.salaryType === 'DAY'

  return (
    <PromptModal
      title={title}
      className="w-[22rem]"
      cancelOnClickOutside
      cancelOnEscape
      onClose={() => onChoose(null)}
      onCancel={() => onChoose(null)}
    >
      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          disabled={!canEdit}
          onClick={() => onChoose('diet')}
        >
          {translations.diet}
          <span className="ml-auto text-stone-500">
            {current(restPeriod.driverDiet)}
          </span>
        </Button>

        {showSalaryDay && (
          <Button
            variant="outline"
            disabled={!canEdit}
            onClick={() => onChoose('salaryDay')}
          >
            {translations.salaryDay}
            <span className="ml-auto text-stone-500">
              {current(restPeriod.salaryDay)}
            </span>
          </Button>
        )}

        {!canEdit && (
          <p className="mt-1 text-sm text-stone-500 italic">
            {translations.locked}
          </p>
        )}
      </div>
    </PromptModal>
  )
}
